import React from "react";
import MainContainer from "../../components/MainContainer";
import SeoHead from "../../components/Seo/SeoHead.js";
import {
  buildBreadcrumbSchema,
  buildPageTitle,
  buildWebsiteSchema,
} from "../../seo/siteSeo.js";

import telegramStars1 from "../../assets/images/telegram-payment-guide/telegram_stars_1.webp";
import telegramStars2 from "../../assets/images/telegram-payment-guide/telegram_stars_2.webp";
import telegramStars3 from "../../assets/images/telegram-payment-guide/telegram_stars_3.webp";

import {
  PageWrapper,
  PageTitle,
  IntroText,
  OrderedList,
  Divider,
  SectionTitle,
  SectionText,
  ImageGrid,
  ImageCenter,
  StepImage,
  ImportantNote,
  TipsBox,
  TipsTitle,
  TipItem,
  TipNumber,
} from "./TelegramPaymentGuidePage.styles";

const TelegramPaymentGuidePage = () => {
  return (
    <MainContainer>
      <SeoHead
        title={buildPageTitle("How to purchase credits using Telegram")}
        description="Step-by-step guide to purchasing credits using Telegram Stars: generate an order on our website, buy Stars in the Telegram App, then pay our bot to complete the purchase."
        path="/telegram-payment-guide"
        jsonLd={[
          buildWebsiteSchema(),
          buildBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Telegram payment guide", path: "/telegram-payment-guide" },
          ]),
        ]}
      />
      <PageWrapper>
        <PageTitle>How to purchase credits using Telegram</PageTitle>
        <IntroText>
          Purchasing our subscription through Telegram is a simple two-step
          process:
        </IntroText>
        <OrderedList>
          <li>
            You purchase Stars from Telegram using your card (or Google Pay,
            Apple Pay, or other methods).
          </li>
          <li>
            You use the Stars you purchased to buy our service (by paying the
            Stars to our bot).
          </li>
        </OrderedList>

        <Divider />

        <SectionTitle>Generate an order on our website</SectionTitle>
        <SectionText>
          On the pricing page, select your desired package and click to pay
          using Telegram.
        </SectionText>

        <Divider />

        <SectionTitle>Buy stars in Telegram App</SectionTitle>
        <SectionText>
          Purchase stars within the Telegram App using your preferred payment
          method. If you already have stars in your balance, you can skip this
          step.
        </SectionText>
        <ImageGrid>
          <StepImage
            src={telegramStars1}
            alt="Telegram Stars purchase screen showing star packages"
            loading="lazy"
          />
          <StepImage
            src={telegramStars2}
            alt="App Store confirmation for buying Telegram Stars"
            loading="lazy"
          />
        </ImageGrid>

        <Divider />

        <SectionTitle>Buy our credits with stars</SectionTitle>
        <ImportantNote>
          <strong>Important Note:</strong>
          <div>
            Once you have enough stars, the next step is to pay the stars to our
            bot to complete the purchase (this is the step most easily
            forgotten).
          </div>
        </ImportantNote>
        <ImageCenter>
          <StepImage
            src={telegramStars3}
            alt="Confirm your purchase screen in Telegram paymentbot"
            loading="lazy"
          />
        </ImageCenter>

        <TipsBox>
          <TipsTitle>Tips</TipsTitle>
          <TipItem>
            <TipNumber>01</TipNumber>
            <div>
              If you have not received your credits, please open the settings
              section in the Telegram App, then click on the{" "}
              <strong>My Stars</strong> area. Check if your purchase was
              unsuccessful or if you missed the final payment step.
            </div>
          </TipItem>
          <TipItem>
            <TipNumber>02</TipNumber>
            <div>
              If your card is declined, try Apple Pay/Google Pay on the mobile
              app, or use the web version of Telegram.
            </div>
          </TipItem>
          <TipItem>
            <TipNumber>03</TipNumber>
            <div>
              The price in Stars may be higher due to Telegram's transaction
              fees. Refer to Telegram's official documentation for details.
            </div>
          </TipItem>
        </TipsBox>
      </PageWrapper>
    </MainContainer>
  );
};

export default TelegramPaymentGuidePage;
